import React, { useMemo } from "react";
import {useSelector } from "react-redux";
import { Row, Col, Card, Button, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../../utilities/tokenizer";
import placeholderImage1 from "../../../images/placeholder.svg";
import {
  SELECTED_SO_CODE_FINO,
} from "../../../constants/soCode";
/**
 * CardItems component displays information for a dropped or single child profile card.
 * Handles both the dropped-child (old/new) and single-child scenarios.
 * @param {Object} card - Child data object
 * @param {number} index - Index of the card in the carousel/list
 * @param {number} totalCards - Total number of cards
 * @param {Function} onNavigate - Optional callback for navigation (old child button)
 */
const CardItems = ({ card, index = 0, totalCards = 1, onNavigate }) => {
  const { t } = useTranslation();
  const { persistItemsReducer } = useSelector((state) => state);
  // Extract dropped child details if present
  const navigate = useNavigate();
  const dropped = card?.droppedChildDetails;
  const sponsorName = card?.sponsorName
  const OldChildName = dropped?.childName;
  const RFLDescription = persistItemsReducer?.selectedSOData?.soCode ===SELECTED_SO_CODE_FINO ?
   dropped?.rfluiDescription: dropped?.rflDescription;
  const communityName = dropped?.childCommunity;
  const countryName = dropped?.childCountry;
  const isSensitive = dropped?.isSensitive;
  const ChildName = card?.childFirstName;
  const ChildCode = card?.childCode;
  const hasDroppedChild = !!dropped;
  const ChildBioRaw = card?.childDescription;
  const newChildImage = card?.childImageURL? card?.childImageURL: placeholderImage1;
  const droppedChildImage = dropped.childImageURL? dropped.childImageURL: placeholderImage1;
  // Memoize bioText for performance and to avoid repeated parsing
  const bioText = useMemo(() => {
    if (!ChildBioRaw) return t("NoChildBio");
    try {
      const bioData = JSON.parse(ChildBioRaw);
      const nameAge = bioData["CFB Name & Age"];
      const countryLivesWith = bioData["CFB Country & Lives With"];
      const parentActivities = bioData["CFB Parent Activities"];
      const bioParts = [nameAge, countryLivesWith, parentActivities].filter(Boolean);
      return bioParts.length > 0 ? bioParts.join(" ") : t("NoChildBio");
    } catch (e) {
      return t("NoChildBio");
    }
  }, [ChildBioRaw, t]);

  // Renders the child count indicator if there are multiple cards
  const renderChildCount = () =>
    totalCards > 1 ? (
      <div className="childcount py-3">
        <strong>
          {t("FieldDropPagination", { index: index + 1, totalCards })}
        </strong>
      </div>
    ) : null;
  const sensitiveFINOChild =  
                      persistItemsReducer?.selectedSOData?.soCode ===SELECTED_SO_CODE_FINO && 
                      isSensitive
                    
  // Render dropped child (old/new) card UI
  if (hasDroppedChild) {
    return (
      <Card className="card double-card-section" key={card.childcode}>
        {/* Old (dropped) child section */}
        <Row className="align-items-center card-left text-center text-md-start">
          <Col xs={12} md={3} className="left-image d-flex justify-content-center">
            <Image src={droppedChildImage} fluid  alt="childImage"/>
          </Col>
          <Col xs={12} md={9} className="px-4">
            <h5 className="text-center text-md-start">{t("OldChildHeading")}</h5>
            <p className="text-center text-md-start">
              {sensitiveFINOChild ? t("OldSensitiveChildDescription",{OldChildName})
              : t("OldChildDescription", { RFLDescription, OldChildName,communityName,countryName,sponsorName})
              }
              </p>
            <div className="text-center text-md-start">
              {/* Button to navigate to old child history */}
              <Button variant="link" onClick={() => onNavigate?.(dropped.childCode)}>
                {t("OldChildButton", { OldChildName })}
              </Button>
            </div>
          </Col>
        </Row>
        <hr />
        {/* New child section */}
        <Row className="align-items-center card-right text-center text-md-end">
          <Col xs={12} md={3} className="right-image order-1 order-md-2 d-flex justify-content-center">
            <Image src={newChildImage} alt="childImage"/>
          </Col>
          <Col xs={12} md={9} className="px-4 order-2 order-md-1">
            <h5 className="text-center text-md-end">{t("NewChildHeading", { ChildName })}</h5>
            <p className="text-center text-md-end">{bioText}</p>
            <div className="text-center text-md-end">
              {/* Button for new child action */}
              <Button variant="warning" onClick={() => navigate(`/communityhomeview/${encrypt(ChildCode)}`)}>
                {t("NewChildButton", { ChildName })}
              </Button>
            </div>
          </Col>
        </Row>
        {renderChildCount()}
      </Card>
    );
  }

  // Render single child card UI (no dropped child)
  return (
    <Card className="card single-section-card" key={card.childcode}>
      <Row className="single-section-layout text-center text-md-center">
        <Col xs={12} md={3} className="single-left-image d-flex justify-content-center">
          <Image src={card?.childImageURL} fluid alt="childImage"/>
        </Col>
        <Col xs={12} md={9} className="single-right-content d-flex flex-column justify-content-center align-items-center align-items-md-start">
          <h5>{t("OldChildHeading")}</h5>
          <p>{t("OldChildDescription", { RFLDescription, OldChildName,communityName,countryName,sponsorName})}</p>
          {/* Button for single child action */}
          <Button variant="warning" onClick={() => onNavigate?.(dropped.childCode)}>{t("OldChildButton", { OldChildName })}</Button>
          {renderChildCount()}
        </Col>
      </Row>
    </Card>
  );
};

export default CardItems;
